import React, { useContext, useMemo, useCallback } from "react";
import { ProductsContext } from "../context/products.context";
import { allIndicatorsSubcategory } from "../data/indicatorsData";
import {
  PiGraduationCap,
  PiBookOpen,
  PiCalculator,
  PiAtom,
  PiUsers,
  PiCertificate,
  PiHandshake,
  PiBriefcase,
  PiPlus
} from "react-icons/pi";
import clsx from "clsx";

// Configuration for indicator icons and aria labels
const INDICATOR_CONFIG = {
  "Graduation Ext": {
    icon: (
      <span className="flex items-center">
        <PiGraduationCap className="text-lg" />
        <PiPlus className="text-xs" />
      </span>
    ),
    ariaLabel: "Graduation Extended"
  },
  "Graduation": {
    icon: <PiGraduationCap className="text-lg" />,
    ariaLabel: "Graduation"
  },
  "Reading": {
    icon: <PiBookOpen className="text-lg" />,
    ariaLabel: "Reading"
  },
  "Math": {
    icon: <PiCalculator className="text-lg" />,
    ariaLabel: "Mathematics"
  },
  "Science": {
    icon: <PiAtom className="text-lg" />,
    ariaLabel: "Science"
  },
  "Non-Trad": {
    icon: <PiUsers className="text-lg" />,
    ariaLabel: "Non-Traditional"
  },
  "Postsecondary Credential": {
    icon: <PiCertificate className="text-lg" />,
    ariaLabel: "Postsecondary Credential"
  },
  "Postsecondary Credits": {
    icon: <PiHandshake className="text-lg" />,
    ariaLabel: "Postsecondary Credits"
  },
  "Work-Based Learning": {
    icon: <PiBriefcase className="text-lg" />,
    ariaLabel: "Work-Based Learning"
  }
};

// Helper to retrieve icon and ariaLabel based on indicator label
function getIndicatorInfo(label) {
  for (const [key, config] of Object.entries(INDICATOR_CONFIG)) {
    if (label.includes(key)) {
      return config;
    }
  }
  return { icon: null, ariaLabel: label };
}

// Memoized individual tab item using a button element.
const IndicatorTabItem = React.memo(
  ({ indicator, isSelected, isFirst, isLast, hideTitles, onTabClick }) => {
    // Split the label into code and description (memoized)
    const [code, description] = useMemo(
      () => indicator.label.split(" - "),
      [indicator.label]
    );

    // Memoize the icon and aria label info
    const { icon, ariaLabel } = useMemo(
      () => getIndicatorInfo(indicator.label),
      [indicator.label]
    );

    return (
      <button
        type="button"
        onClick={() => onTabClick(indicator)}
        className={clsx(
          "px-3 py-2 w-full text-left transition-colors duration-200 border-l-4 outline-none border-b-zinc-200 border-y border-y-transparent",
          isFirst && !isSelected && "rounded-tl-lg",
          isLast && !isSelected && "rounded-bl-lg",
          isSelected
            ? "bg-white border-l-blue-500 text-blue-700 border-r-white border-r"
            : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 border-r-zinc-200 border-r"
        )}
        aria-label={ariaLabel}
      >
        {hideTitles}
        <div className="flex items-center text-zinc-500">
          {indicator.category === "Perkins V Indicators" && icon && (
            <div className="flex-shrink-0 mr-2 w-6 h-6 flex items-center justify-center">
              {icon}
            </div>
          )}
          <div className="flex flex-col text-zinc-700">
            {description && (
              <span className="text-sm font-semibold">
                {description.trim()}
              </span>
            )}
            <span className="text-xs text-zinc-600">{code}</span>
          </div>
        </div>
      </button>
    );
  }
);

function TopTabBarIndicatorsWithIcons({ hideTitles }) {
  const { indicatorsCategory, indicatorsSubcategory, setIndicatorsSubcategory } =
    useContext(ProductsContext);

  // Memoize filtered indicators so that recalculation only occurs when the category changes.
  const filteredIndicators = useMemo(
    () =>
      allIndicatorsSubcategory.filter(
        (f) => f.category === indicatorsCategory.value
      ),
    [indicatorsCategory.value]
  );

  // Get the currently selected indicator
  const selectedIndicator = indicatorsSubcategory;

  // Memoize click handler
  const handleTabClick = useCallback(
    (indicator) => {
      setIndicatorsSubcategory(indicator);
    },
    [setIndicatorsSubcategory]
  );

  return (
    <div className="flex flex-col max-w-[250px] select-none">
      <div className="flex flex-col">
        {filteredIndicators.map((indicator, index) => {
          const isFirst = index === 0;
          const isLast = index === filteredIndicators.length - 1;
          const isSelected =
  selectedIndicator?.label?.trim() === indicator.label.trim();
          return (
            <IndicatorTabItem
              key={indicator.label}
              indicator={indicator}
              isFirst={isFirst}
              isLast={isLast}
              isSelected={isSelected}
              hideTitles={hideTitles}
              onTabClick={handleTabClick}
            />
          );
        })}
      </div>
    </div>
  );
}

export default React.memo(TopTabBarIndicatorsWithIcons);